/**
 * Cliente de los workers del decodificador (004 · US1 · T028; pool por el incidente del piloto).
 *
 * **Pool de hasta `concurrency` workers** (por defecto 2 si el equipo tiene ≥ 4 núcleos, si no 1).
 * Con un solo worker, un teléfono en el que cada decodificación cuesta ~300 ms solo alcanzaba ~3
 * lecturas en la ráfaga de 6 frames × 200 ms (se exigen 4: `TOO_FEW_FRAMES`). Dos workers en
 * núcleos distintos doblan las lecturas por segundo sin tocar las opciones de zxing ni la
 * reducción a 400×400. En los teléfonos rápidos no cambia nada: un worker ya da abasto y el
 * segundo casi no se usa.
 *
 * Una petición en vuelo **por worker** como máximo: decodificar más rápido de lo que responden solo
 * acumularía fotogramas viejos; si llega uno nuevo con todos ocupados, **se descarta el nuevo**
 * (el bucle de captura ya lo evita preguntando `inFlight()` antes de preparar la imagen).
 *
 * **Orden:** con dos workers las respuestas pueden volver en desorden (el segundo fotograma puede
 * decodificarse antes que el primero). Este cliente **no** reordena: cada promesa resuelve cuando
 * su worker contesta. Quien entrega las lecturas en orden de captura es `scanner.ts`, que es el
 * que conoce el orden (y el servidor lo valida: `OUT_OF_ORDER`).
 *
 * El buffer RGBA se **transfiere**, no se copia (640 KB por fotograma con la reducción de `roi.ts`).
 * Tras transferirlo, el `ImageData` de origen queda inutilizable: por eso el bucle de captura crea
 * uno nuevo en cada vuelta.
 *
 * La fábrica del worker es inyectable para poder probar el bucle sin WebAssembly ni `Worker`.
 */
import type { DecodeRequest, DecoderMessage } from './decoder-protocol';

/** Lo mínimo de `Worker` que usa el cliente (un doble de pruebas lo implementa en 10 líneas). */
export interface DecoderWorkerLike {
  postMessage(message: DecodeRequest, transfer: Transferable[]): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<DecoderMessage>) => void): void;
  terminate(): void;
}

export interface DecodeOutcome {
  /** Texto exacto del QR, o `null` si el fotograma no traía ninguno legible. */
  text: string | null;
  /** Lado del símbolo en la imagen reducida; `null` sin lectura. Telemetría (research R-4). */
  symbolSidePx: number | null;
  decodeMs: number;
}

export interface Decoder {
  /**
   * `null` si todos los workers estaban ocupados (el fotograma se descarta), si falló la lectura o
   * si el decodificador se cerró. Nunca rechaza.
   */
  decode(image: ImageData): Promise<DecodeOutcome | null>;
  close(): void;
  /** Peticiones que admite a la vez (workers del pool). Sin el campo, el bucle asume 1. */
  readonly concurrency?: number;
  /** Peticiones en vuelo ahora mismo. */
  inFlight?(): number;
}

/** Worker real: módulo ES con la URL resuelta por Vite (`worker.format: 'es'` en la config). */
function defaultWorker(): DecoderWorkerLike {
  return new Worker(new URL('./decoder.worker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as DecoderWorkerLike;
}

/** Tope de workers del pool: más no aporta (y cada uno carga su propio WASM). */
export const MAX_DECODER_WORKERS = 2;

/**
 * Workers por defecto: 2 si el equipo dice tener ≥ 4 núcleos lógicos, si no 1. En un equipo de 2
 * núcleos el segundo worker le quitaría CPU al hilo principal, que también tiene que preparar cada
 * fotograma (`drawReducedRoi`) y pintar la interfaz.
 */
export function defaultDecoderConcurrency(
  hardwareConcurrency: number | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.hardwareConcurrency,
): number {
  if (typeof hardwareConcurrency !== 'number' || !Number.isFinite(hardwareConcurrency)) {
    return 1;
  }
  return hardwareConcurrency >= 4 ? MAX_DECODER_WORKERS : 1;
}

export interface DecoderOptions {
  createWorker?: () => DecoderWorkerLike;
  /** Workers del pool; por defecto `defaultDecoderConcurrency()`. Se recorta a 1…`MAX_DECODER_WORKERS`. */
  concurrency?: number;
}

interface Slot {
  worker: DecoderWorkerLike;
  busy: boolean;
}

export function createDecoder(options: DecoderOptions = {}): Decoder {
  const factory = options.createWorker ?? defaultWorker;
  const requested = options.concurrency ?? defaultDecoderConcurrency();
  const size = Math.min(
    MAX_DECODER_WORKERS,
    Math.max(1, Number.isFinite(requested) ? Math.floor(requested) : 1),
  );
  const pending = new Map<number, { slot: Slot; resolve: (outcome: DecodeOutcome | null) => void }>();
  let nextId = 1;
  let closed = false;
  let inFlight = 0;

  const slots: Slot[] = [];
  for (let index = 0; index < size; index += 1) {
    const slot: Slot = { worker: factory(), busy: false };
    slot.worker.addEventListener('message', (event: MessageEvent<DecoderMessage>) => {
      const message = event.data;
      const entry = pending.get(message.id);
      // Solo cuenta la respuesta del worker que recibió la petición (los ids son únicos en el pool).
      if (entry === undefined || entry.slot !== slot) {
        return;
      }
      pending.delete(message.id);
      slot.busy = false;
      inFlight -= 1;
      if (message.type === 'decoded') {
        entry.resolve({
          text: message.text,
          symbolSidePx: message.symbolSidePx,
          decodeMs: message.decodeMs,
        });
        return;
      }
      entry.resolve(null);
    });
    slots.push(slot);
  }

  return {
    concurrency: size,
    inFlight: () => inFlight,
    decode(image) {
      if (closed) {
        return Promise.resolve(null);
      }
      const slot = slots.find((candidate) => !candidate.busy);
      if (slot === undefined) {
        return Promise.resolve(null);
      }
      slot.busy = true;
      inFlight += 1;
      const id = nextId;
      nextId += 1;
      return new Promise<DecodeOutcome | null>((resolve) => {
        pending.set(id, { slot, resolve });
        try {
          slot.worker.postMessage(
            { type: 'decode', id, data: image.data, width: image.width, height: image.height },
            [image.data.buffer],
          );
        } catch {
          pending.delete(id);
          slot.busy = false;
          inFlight -= 1;
          resolve(null);
        }
      });
    },
    close() {
      if (closed) {
        return;
      }
      closed = true;
      for (const entry of pending.values()) {
        entry.resolve(null);
      }
      pending.clear();
      inFlight = 0;
      for (const slot of slots) {
        slot.busy = false;
        slot.worker.terminate();
      }
    },
  };
}
